import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Building2, ExternalLink, LogOut, Menu, Moon, PanelLeft, Package, Receipt, Search, ShieldCheck, Sun, User, Users } from 'lucide-react';
import type { SearchHit } from '@/types/admin';
import { adminServices } from '@/services';
import { toast, useAuthStore, useThemeStore } from '@/app/stores';
import { useAsync } from '@/hooks/useAsync';
import { useDebounce } from '@/hooks/useDebounce';
import { Dropdown } from '@/components/ui/Dropdown';
import { roleLabel } from '@/lib/permissions';
import { formatDate, initials } from '@/lib/format';
import { cn } from '@/lib/cn';
import { AllAppsLink } from '@/components/AllAppsLink';
import { useAdminSession } from './session';

const iconBtn = 'relative grid size-10 place-items-center rounded-lg text-foreground hover:bg-muted';

const hitIcon = { product: Package, order: Receipt, customer: Users } as const;

/** Global search across this tenant's products, orders and customers (server-scoped in the real API). */
function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const dq = useDebounce(q, 250);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    adminServices.dashboard.search(dq).then((r) => live && setHits(r)).catch(() => live && setHits([]));
    return () => { live = false; };
  }, [dq]);

  useEffect(() => {
    const down = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement)) { e.preventDefault(); input.current?.focus(); }
    };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, []);

  const go = (to: string) => { setOpen(false); setQ(''); nav(to); };

  return (
    <div ref={root} className="relative w-full max-w-md" role="search">
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input
        ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && hits[0]) go(hits[0].to); }}
        placeholder="Search products, orders, customers…" aria-label="Search admin"
        className="h-10 w-full rounded-lg border border-input bg-muted/50 pl-9 pr-9 text-sm placeholder:text-muted-foreground focus:border-ring focus:bg-card focus:outline-none focus:ring-2 focus:ring-ring/25"
      />
      <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-border px-1.5 text-[10px] font-semibold text-muted-foreground lg:block">/</kbd>
      {open && q.trim().length >= 2 && (
        <div className="anim-pop absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-border bg-card shadow-pop">
          {hits.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No matches for “{q}”.</p> : (
            <ul>
              {hits.map((h) => {
                const Icon = hitIcon[h.kind];
                return (
                  <li key={`${h.kind}-${h.id}`}>
                    <button type="button" onClick={() => go(h.to)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted">
                      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary"><Icon className="size-4" /></span>
                      <span className="min-w-0"><span className="block truncate text-sm font-semibold">{h.title}</span><span className="block truncate text-xs text-muted-foreground">{h.subtitle}</span></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const READ_KEY = 'mce.admin.notifs.read';
const readSet = (): string[] => { try { return JSON.parse(localStorage.getItem(READ_KEY) ?? '[]'); } catch { return []; } };

function Notifications() {
  const nav = useNavigate();
  const state = useAsync(() => adminServices.dashboard.notifications(), []);
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState<string[]>(readSet);
  const root = useRef<HTMLDivElement>(null);
  const items = state.data ?? [];
  const unread = items.filter((n) => !read.includes(n.id)).length;

  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', down); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);

  const markAll = () => { const ids = items.map((n) => n.id); setRead(ids); try { localStorage.setItem(READ_KEY, JSON.stringify(ids)); } catch { /* ignore */ } };

  return (
    <div ref={root} className="relative">
      <button className={iconBtn} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Bell className="size-5" />
        {unread > 0 && <span className="absolute right-1.5 top-1.5 grid min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold leading-4 text-accent-foreground">{unread}</span>}
      </button>
      {open && (
        <div className="anim-pop fixed inset-x-3 top-16 z-50 overflow-hidden rounded-xl border border-border bg-card shadow-pop sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96">
          <div className="flex items-center justify-between border-b border-border px-4 py-3"><p className="text-sm font-bold">Notifications</p>{unread > 0 && <button onClick={markAll} className="text-xs font-semibold text-primary hover:underline">Mark all read</button>}</div>
          {state.loading ? <p className="p-4 text-sm text-muted-foreground">Loading…</p> : state.error ? <p className="p-4 text-sm text-danger">Could not load notifications.</p> : items.length === 0 ? <p className="p-4 text-sm text-muted-foreground">You&apos;re all caught up.</p> : (
            <ul className="max-h-96 divide-y divide-border overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <button className="flex w-full gap-3 px-4 py-3 text-left hover:bg-muted" onClick={() => { setOpen(false); nav(n.to); }}>
                    <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', read.includes(n.id) ? 'bg-input' : n.tone === 'warning' ? 'bg-warning' : n.tone === 'success' ? 'bg-success' : 'bg-info')} aria-hidden />
                    <span className="min-w-0"><span className="block text-sm font-semibold">{n.title}</span><span className="block truncate text-xs text-muted-foreground">{n.body}</span><span className="text-[11px] text-muted-foreground">{formatDate(n.at)}</span></span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminHeader({ onOpenMenu, onToggleSidebar }: { onOpenMenu: () => void; onToggleSidebar: () => void }) {
  const actor = useAdminSession((s) => s.actor)!;
  const { logout } = useAuthStore();
  const { mode, toggle } = useThemeStore();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-card/90 backdrop-blur-md">
      <div className="flex h-16 items-center gap-2 px-3 sm:gap-3 sm:px-6">
        <button className={cn(iconBtn, 'lg:hidden')} onClick={onOpenMenu} aria-label="Open navigation"><Menu className="size-5" /></button>
        <button className={cn(iconBtn, 'hidden lg:grid')} onClick={onToggleSidebar} aria-label="Collapse or expand sidebar"><PanelLeft className="size-5" /></button>
        <div className="min-w-0 flex-1"><GlobalSearch /></div>
        <div className="flex items-center gap-0.5">
          <AllAppsLink />
          <Dropdown
            label="Store menu" align="right"
            trigger={<span className="hidden items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted md:flex"><Building2 className="size-4 text-primary" /><span className="max-w-32 truncate">{actor.tenant.name}</span></span>}
            items={[
              { label: `${actor.tenant.name} (current)`, icon: <Building2 className="size-4" />, onSelect: () => undefined },
              { label: 'Open storefront', icon: <ExternalLink className="size-4" />, to: '/', divider: true },
              { label: 'Switch store…', icon: <Building2 className="size-4" />, onSelect: () => toast.info('Single store on this plan', 'Multi-store switching arrives with the platform console.') },
            ]}
          />
          <button className={iconBtn} onClick={toggle} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>{mode === 'dark' ? <Sun className="size-5" /> : <Moon className="size-5" />}</button>
          <Notifications />
          <Dropdown
            label="Account menu"
            trigger={<span className="flex items-center gap-2 rounded-lg p-1 hover:bg-muted"><span className="grid size-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{initials(actor.user.name)}</span><span className="hidden text-left leading-tight lg:block"><span className="block max-w-28 truncate text-sm font-semibold">{actor.user.name}</span><span className="block text-[11px] text-muted-foreground">{roleLabel[actor.user.role]}</span></span></span>}
            items={[
              { label: actor.user.email, icon: <User className="size-4" />, onSelect: () => undefined },
              { label: 'View storefront', icon: <ExternalLink className="size-4" />, to: '/', divider: true },
              ...(actor.impersonating ? [{ label: 'Super Admin console', icon: <ShieldCheck className="size-4" />, to: '/super-admin' }] : []),
              { label: 'Sign out', icon: <LogOut className="size-4" />, danger: true, divider: !actor.impersonating, onSelect: async () => { await logout(); useAdminSession.getState().reset(); toast.info('Signed out'); nav('/login'); } },
            ]}
          />
        </div>
      </div>
      {actor.impersonating && (
        <p className="border-t border-warning/30 bg-warning-soft px-4 py-1.5 text-center text-xs font-semibold text-warning">
          Platform support access: you are viewing {actor.tenant.name} as a platform admin. In production every action here is audit-logged. <Link to="/super-admin" className="underline">Open Super Admin</Link>
        </p>
      )}
    </header>
  );
}

